/**
 * v2 Profile Rewrite — task-focused workflow.
 * One section shown at a time, right sidebar shows 5-step progress, big
 * "YOUR NEXT ACTION" hero, comprehensive Before/After per section, and a
 * "Back / I've updated it — Continue" footer. Progress persists in
 * localStorage (per analysis id).
 *
 * The 5 canonical sections (per user's design):
 *   1. Headline · 2. About · 3. Experience · 4. Featured · 5. Skills
 */
import React, { useMemo, useState } from 'react';
import { CheckCircle2, Copy, ChevronLeft, ArrowRight, Info, ChevronDown, ChevronUp, Layers, Loader2, RotateCcw, RefreshCw, X, Eye, EyeOff, Target, Sparkles, Wand2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { markSectionComplete } from '../../lib/rewriteProgress';
import { wordDiff } from '../../lib/wordDiff';

// -------------------- helpers --------------------
const REWRITE_SECTIONS = [
  { key: 'headline', label: 'Headline', minutes: 3, impact: 'Highest profile impact' },
  { key: 'about', label: 'About', minutes: 6, impact: 'Highest recruiter dwell time' },
  { key: 'experience', label: 'Experience', minutes: 10, impact: 'Longest reader engagement' },
  { key: 'featured', label: 'Featured', minutes: 4, impact: 'Where readers convert' },
  { key: 'skills', label: 'Skills', minutes: 3, impact: 'Search + endorsements' },
];

function copyText(text) {
  if (!text) return;
  navigator.clipboard.writeText(text).then(
    () => toast.success('Copied'),
    () => toast.error('Copy failed'),
  );
}

function DiffText({ tokens, kind }) {
  // Render tokenised diff output. `kind` is only used to pick the
  // right chip style for changed tokens.
  const changedClass = kind === 'after'
    ? 'bg-emerald-500/20 text-emerald-900 dark:text-emerald-200 rounded px-0.5'
    : 'bg-rose-500/15 text-rose-800 dark:text-rose-200 line-through rounded px-0.5';
  return (
    <span>
      {tokens.map((t, i) => {
        if (t.kind === 'equal') return <span key={i}>{t.ws}{t.text}</span>;
        return <span key={i}>{t.ws}<span className={changedClass}>{t.text}</span></span>;
      })}
    </span>
  );
}

function BeforeAfter({ before, after, beforeLabel = 'Current on LinkedIn', afterLabel = 'Blayzrr rewrite', onCopy, testid }) {
  const [showDiff, setShowDiff] = useState(true);
  const canDiff = !!(before && after);
  const diff = canDiff && showDiff ? wordDiff(before, after) : null;
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid={testid}>
      <div className="rounded-xl border border-dashed border-border bg-secondary/50 p-3.5">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <p className="text-[10px] font-mono-edge tracking-[0.18em] uppercase text-muted-foreground">{beforeLabel}</p>
          {canDiff && (
            <button
              type="button"
              onClick={() => setShowDiff((v) => !v)}
              className="inline-flex items-center gap-1 rounded-full bg-secondary hover:bg-secondary/70 text-[10.5px] text-muted-foreground hover:text-foreground px-2 py-0.5 transition-colors"
              data-testid={`${testid || 'ba'}-toggle-diff`}
            >
              {showDiff ? <EyeOff size={10} /> : <Eye size={10} />} {showDiff ? 'Hide diff' : 'Show diff'}
            </button>
          )}
        </div>
        {before ? (
          <p className="text-[13px] italic text-muted-foreground whitespace-pre-wrap leading-relaxed">
            {diff ? <DiffText tokens={diff.before} kind="before" /> : before}
          </p>
        ) : (
          <p className="text-[12.5px] text-muted-foreground italic">Blank on LinkedIn today — the rewrite becomes your starting draft.</p>
        )}
      </div>
      <div className="rounded-xl border border-primary/40 bg-primary/[0.06] p-3.5">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] font-mono-edge tracking-[0.18em] uppercase text-primary">{afterLabel}</p>
          {onCopy && (
            <button
              type="button"
              onClick={() => onCopy(after)}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 hover:bg-primary/20 text-primary px-2 py-0.5 text-[11px] font-medium transition-colors"
              data-testid={`${testid || 'ba'}-copy`}
            >
              <Copy size={11} /> Copy
            </button>
          )}
        </div>
        <p className="text-[13px] whitespace-pre-wrap leading-relaxed">
          {diff ? <DiffText tokens={diff.after} kind="after" /> : after}
        </p>
        {canDiff && showDiff && (
          <p className="mt-2 text-[10.5px] text-muted-foreground flex flex-wrap items-center gap-2" data-testid={`${testid || 'ba'}-diff-legend`}>
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-rose-500/40" /> removed</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-emerald-500/40" /> added</span>
          </p>
        )}
      </div>
    </div>
  );
}

function Collapse({ title, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-border pt-2" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 text-[13px] font-medium hover:text-primary transition-colors py-1"
      >
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {title}
      </button>
      {open && <div className="mt-2 pl-1 text-[12.5px] text-muted-foreground space-y-2">{children}</div>}
    </div>
  );
}

// -------------------- section renderers --------------------

// Small side-by-side compare panel that lives inside the Headline
// and About rewrite sections. It shows the current tone next to any
// previously-generated alternate tone from tone_variants. If the
// selected alternate hasn't been generated yet, offers a one-click
// button to kick off a tone regeneration via onRegenerateTone.
const SLIDER_LABEL = { strict: 'Strict', balanced: 'Balanced', high_storytelling: 'High-storytelling' };
function pickPrimary(section, field) {
  if (!section) return '';
  if (field === 'headline') {
    const opts = section.options || [];
    const rec = opts[section.recommendedOption ?? 0] || opts[0] || {};
    return rec.text || '';
  }
  if (field === 'about') return section.rewrittenCopy || '';
  return '';
}
function ToneComparePanel({ audit, field, toneVariants, onRegenerateTone, regenerating }) {
  const currentSlider = audit.languageSlider;
  const archivedSliders = Object.keys(toneVariants || {}).filter((k) => k !== currentSlider);
  const otherSliders = ['strict', 'balanced', 'high_storytelling'].filter((s) => s !== currentSlider);
  const defaultPick = archivedSliders[0] || otherSliders[0] || null;
  const [pick, setPick] = useState(defaultPick);
  const [open, setOpen] = useState(false);
  if (!currentSlider || !onRegenerateTone) return null;
  const compareVariant = pick ? toneVariants?.[pick] : null;
  const currentText = pickPrimary(field === 'headline' ? audit.headline : audit.about, field);
  const compareText = pickPrimary(field === 'headline' ? compareVariant?.headline : compareVariant?.about, field);
  return (
    <Card className="border-primary/25 bg-primary/[0.03]" data-testid={`rewrite-${field}-compare-panel`}>
      <CardContent className="p-3.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 text-left"
          data-testid={`rewrite-${field}-compare-toggle`}
        >
          <div className="min-w-0">
            <p className="text-[10.5px] font-mono-edge tracking-[0.18em] uppercase text-primary flex items-center gap-1.5">
              <Layers size={11} /> Compare tones
            </p>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              See this rewrite next to another tone before you pick.
            </p>
          </div>
          <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <>
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5" data-testid={`rewrite-${field}-compare-picker`}>
              <span className="text-[10px] text-muted-foreground uppercase tracking-[0.14em] font-mono-edge">Vs</span>
              {otherSliders.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setPick(s)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    pick === s
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:text-foreground hover:border-border/70'
                  }`}
                  data-testid={`rewrite-${field}-compare-pick-${s}`}
                >
                  {SLIDER_LABEL[s]}
                  {!archivedSliders.includes(s) && (
                    <span className="ml-1 text-[9.5px] text-amber-500 uppercase">·new</span>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-3 grid md:grid-cols-2 gap-2.5" data-testid={`rewrite-${field}-compare-grid`}>
              <div className="rounded-lg border border-primary/25 bg-primary/[0.06] p-3">
                <p className="text-[10px] font-mono-edge tracking-[0.14em] uppercase text-primary mb-1.5">
                  Current — {SLIDER_LABEL[currentSlider] || 'Balanced'}
                </p>
                <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap">
                  {currentText || <span className="text-muted-foreground italic">— not available —</span>}
                </p>
              </div>
              {compareText ? (
                <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.05] p-3">
                  <p className="text-[10px] font-mono-edge tracking-[0.14em] uppercase text-emerald-600 dark:text-emerald-300 mb-1.5">
                    Compare — {SLIDER_LABEL[pick] || pick}
                  </p>
                  <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap">{compareText}</p>
                </div>
              ) : (
                <div
                  className="rounded-lg border border-dashed border-primary/30 bg-white/[0.03] p-3 flex flex-col items-start gap-2"
                  data-testid={`rewrite-${field}-compare-generate`}
                >
                  <p className="text-[10.5px] uppercase tracking-[0.14em] font-mono-edge text-muted-foreground">
                    {pick ? `${SLIDER_LABEL[pick]} not generated yet` : 'Pick a tone'}
                  </p>
                  <p className="text-[12px] text-muted-foreground">
                    Kick off a rewrite in the {SLIDER_LABEL[pick] || pick} tone and it&rsquo;ll appear here for comparison. Same profile, no re-upload.
                  </p>
                  <Button
                    size="sm"
                    className="rounded-full mt-1"
                    disabled={!!regenerating || !pick}
                    onClick={() => pick && onRegenerateTone(pick)}
                    data-testid={`rewrite-${field}-compare-generate-btn`}
                  >
                    {regenerating ? (
                      <><Loader2 size={12} className="mr-1.5 animate-spin" /> Generating…</>
                    ) : (
                      <><RefreshCw size={12} className="mr-1.5" /> Generate {SLIDER_LABEL[pick] || pick}</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}


// -------------------- Prompt submission box --------------------

/**
 * Per-section free-text prompt input shown at the bottom of each
 * Profile Rewrite step. Lets the visitor steer the LLM with a specific
 * instruction ("please weave in my AWS cert", "target Director-level
 * roles", "quantify the impact") without re-running the full audit.
 *
 * Backend contract: ``POST /analyze/{id}/regenerate-section`` with
 * ``{section, user_prompt}``. The parent component holds the actual
 * request plumbing in ``onRegenerateSection`` so this box stays
 * presentational.
 */
const SUGGESTED_PROMPTS = {
  headline: [
    'Add my latest certification',
    'Target a Director-level role',
    'Highlight my team-leadership',
  ],
  about: [
    'Add my latest certification',
    'Target a Director-level role',
    'Highlight my team-leadership',
  ],
};

function SectionPromptBox({ section, busy, onRegenerateSection }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const suggestions = SUGGESTED_PROMPTS[section] || [];

  const canSubmit = !!onRegenerateSection && !busy && !!prompt.trim();

  const handleSubmit = () => {
    const text = prompt.trim();
    if (!canSubmit) return;
    onRegenerateSection(section, { userPrompt: text });
    setPrompt('');
    setOpen(false);
  };

  const testBase = `rewrite-section-prompt-${section}`;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group mt-4 w-full rounded-xl border-2 border-dashed border-primary/40 bg-primary/[0.04] hover:bg-primary/[0.08] hover:border-primary/70 px-4 py-3.5 flex items-center justify-between gap-3 transition-colors text-left"
        data-testid={`${testBase}-open`}
      >
        <span className="flex items-center gap-2.5 min-w-0">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary group-hover:bg-primary/25 transition-colors">
            <Wand2 size={15} aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block text-[13.5px] font-semibold text-foreground">
              Add a specific request for the {section === 'headline' ? 'Headline' : 'About'}
            </span>
            <span className="block text-[11.5px] text-muted-foreground truncate">
              Add a skill, course, or any detail you want woven in.
            </span>
          </span>
        </span>
        <ChevronDown size={16} className="text-primary shrink-0" aria-hidden="true" />
      </button>
    );
  }

  return (
    <Card className="mt-4 border-primary/40 bg-primary/[0.03]" data-testid={testBase}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-xs font-mono-edge tracking-[0.2em] uppercase text-primary">
            Specific request
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setOpen(false); setPrompt(''); }}
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            data-testid={`${testBase}-cancel`}
          >
            Cancel
          </Button>
        </div>

        {suggestions.length > 0 && (
          <div
            className="mb-3 flex flex-wrap gap-1.5"
            data-testid={`${testBase}-suggestions`}
          >
            {suggestions.map((s, i) => (
              <button
                key={s}
                type="button"
                onClick={() => setPrompt(s)}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-background/70 px-2.5 py-1 text-[11.5px] text-foreground/80 hover:border-primary/60 hover:bg-primary/10 hover:text-foreground transition-colors disabled:opacity-50"
                data-testid={`${testBase}-chip-${i}`}
              >
                <Sparkles size={10} className="text-primary" aria-hidden="true" />
                {s}
              </button>
            ))}
          </div>
        )}

        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={
            section === 'headline'
              ? 'e.g. Lean the headline towards product management roles at Series-B SaaS companies.'
              : 'e.g. Please weave in the Stanford Online course I just finished on Operations Research.'
          }
          rows={3}
          maxLength={1200}
          className="resize-none text-sm"
          data-testid={`${testBase}-input`}
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <p className="text-[11px] text-muted-foreground">
            Only this section rewrites — your other sections stay as they are.
          </p>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid={`${testBase}-submit`}
          >
            {busy ? (
              <>
                <Loader2 size={13} className="mr-1.5 animate-spin" />
                Rewriting…
              </>
            ) : (
              <>
                <Sparkles size={13} className="mr-1.5" />
                Apply
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Chip shown on top of a regenerated section when the user supplied a
 * steering prompt. Confirms the prompt was honoured and gives them a
 * visible trail of what they asked for — so "regenerated but nothing
 * changed" never happens silently.
 */
function AppliedPromptChip({ text, testid }) {
  const clean = (text || "").trim();
  if (!clean) return null;
  return (
    <div
      className="mb-3 flex items-start gap-2 rounded-xl border border-indigo-400/40 bg-indigo-500/[0.07] px-3 py-2"
      data-testid={testid || "applied-prompt-chip"}
    >
      <Sparkles size={14} className="mt-0.5 shrink-0 text-indigo-600 dark:text-indigo-300" />
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
          Reflects your prompt
        </p>
        <p className="text-[12.5px] text-indigo-900/90 dark:text-indigo-100/90 leading-snug break-words">
          "{clean}"
        </p>
      </div>
    </div>
  );
}

function HeadlineSection({ audit, toneVariants, onRegenerateTone, regenerating, onRegenerateSection, regeneratingSection }) {
  const h = audit.headline || {};
  const options = h.options || [];
  const recIdx = h.recommendedOption ?? 0;
  const rec = options[recIdx];
  const alts = options.filter((_, i) => i !== recIdx);
  const busy = regeneratingSection === 'headline';
  return (
    <div className="space-y-4">
      <Card className="border-border shadow-sm"><CardContent className="p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div>
            <p className="text-sm font-semibold">Recommended headline</p>
            <p className="text-[11.5px] text-muted-foreground">Balanced for clarity and LinkedIn search</p>
          </div>
          <div className="flex items-center gap-2">
            {onRegenerateSection && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRegenerateSection('headline')}
                disabled={busy}
                data-testid="rewrite-regenerate-headline"
                className="h-8 gap-1.5"
                title="Generate fresh headline options against the current formula"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                Regenerate
              </Button>
            )}
            <p className="text-[11px] text-primary font-mono-edge tabular-nums">{rec?.characterCount || 0} / {audit.headlineCharLimit || 220}</p>
          </div>
        </div>
        <AppliedPromptChip text={h.userSteerApplied} testid="rewrite-headline-prompt-chip" />
        {rec ? (
          <BeforeAfter
            before={h.current}
            after={rec.text}
            afterLabel="Recommended headline"
            onCopy={copyText}
            testid="rewrite-headline-ba"
          />
        ) : (
          <p className="text-sm text-muted-foreground">No headline rewrite generated for this audit.</p>
        )}
        {rec && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => copyText(rec.text)}
            className="mt-3 w-full"
            data-testid="rewrite-headline-copy-primary"
          >
            <Copy size={13} className="mr-2" /> Copy headline
          </Button>
        )}
      </CardContent></Card>

      {rec?.logic && (
        <Collapse title="Why this works" testid="rewrite-headline-why">
          <p>{rec.logic}</p>
          {rec.keywords?.length > 0 && <p>Keywords: <span className="text-foreground">{rec.keywords.join(', ')}</span></p>}
          {rec.audience && <p>Audience: <span className="text-foreground">{rec.audience}</span></p>}
        </Collapse>
      )}

      {alts.length > 0 && (
        <Collapse title={`See ${alts.length} alternative${alts.length > 1 ? 's' : ''}`} testid="rewrite-headline-alts">
          <div className="space-y-2.5">
            {alts.map((o, i) => (
              <div key={i} className="rounded-lg border border-border p-3 bg-card">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <Badge variant="secondary" className="text-[10px] capitalize">{(o.type || '').replace(/_/g, ' ')}</Badge>
                  <span className="text-[11px] text-muted-foreground">{o.characterCount}/{audit.headlineCharLimit} chars</span>
                  <button
                    type="button"
                    onClick={() => copyText(o.text)}
                    className="ml-auto inline-flex items-center gap-1 rounded-full bg-primary/10 hover:bg-primary/20 text-primary px-2 py-0.5 text-[11px] font-medium transition-colors"
                  >
                    <Copy size={11} /> Copy
                  </button>
                </div>
                <p className="text-[13px] font-medium text-foreground whitespace-pre-wrap">{o.text}</p>
                {o.logic && <p className="mt-1 text-[12px] text-muted-foreground">{o.logic}</p>}
              </div>
            ))}
          </div>
        </Collapse>
      )}

      <ToneComparePanel
        audit={audit}
        field="headline"
        toneVariants={toneVariants}
        onRegenerateTone={onRegenerateTone}
        regenerating={regenerating}
      />

      <SectionPromptBox
        section="headline"
        busy={regeneratingSection === 'headline'}
        onRegenerateSection={onRegenerateSection}
      />
    </div>
  );
}

function AboutSection({ audit, toneVariants, onRegenerateTone, regenerating, onRegenerateSection, regeneratingSection }) {
  const a = audit.about || {};
  const busy = regeneratingSection === 'about';
  return (
    <div className="space-y-4">
      <Card className="border-border shadow-sm"><CardContent className="p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div>
            <p className="text-sm font-semibold">Complete About rewrite</p>
            <p className="text-[11.5px] text-muted-foreground">Positioning-led · scannable · keyword-optimised</p>
          </div>
          <div className="flex items-center gap-2">
            {onRegenerateSection && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRegenerateSection('about')}
                disabled={busy}
                data-testid="rewrite-regenerate-about"
                className="h-8 gap-1.5"
                title="Generate a fresh About rewrite against the current formula"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                Regenerate
              </Button>
            )}
            <p className="text-[11px] text-primary font-mono-edge tabular-nums">{a.characterCount || 0} / {audit.aboutCharLimit || 2600}</p>
          </div>
        </div>
        <AppliedPromptChip text={a.userSteerApplied} testid="rewrite-about-prompt-chip" />
        {a.rewrittenCopy ? (
          <BeforeAfter
            before={audit.about?.current || audit.rawSections?.about}
            after={a.rewrittenCopy}
            afterLabel="Rewritten About"
            onCopy={copyText}
            testid="rewrite-about-ba"
          />
        ) : (
          <p className="text-sm text-muted-foreground">No About rewrite generated.</p>
        )}
        {a.rewrittenCopy && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => copyText(a.rewrittenCopy)}
            className="mt-3 w-full"
            data-testid="rewrite-about-copy-primary"
          >
            <Copy size={13} className="mr-2" /> Copy About
          </Button>
        )}
      </CardContent></Card>

      {a.diagnosis && (
        <Collapse title="Why this works" testid="rewrite-about-why">
          <p>{a.diagnosis}</p>
          {a.keywordsUsed?.length > 0 && <p>Keywords woven in: <span className="text-foreground">{a.keywordsUsed.join(', ')}</span></p>}
        </Collapse>
      )}

      {a.confirmationRequired?.length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/[0.06] p-3.5" data-testid="rewrite-about-confirm">
          <p className="text-[12.5px] font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-1.5"><Info size={13} /> Confirm before publishing</p>
          <ul className="mt-1.5 list-disc pl-4 text-[12.5px] text-amber-800 dark:text-amber-200 space-y-1">
            {a.confirmationRequired.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}

      <ToneComparePanel
        audit={audit}
        field="about"
        toneVariants={toneVariants}
        onRegenerateTone={onRegenerateTone}
        regenerating={regenerating}
      />

      <SectionPromptBox
        section="about"
        busy={regeneratingSection === 'about'}
        onRegenerateSection={onRegenerateSection}
      />
    </div>
  );
}

function ExperienceSection({ audit }) {
  const items = audit.experienceRewrites || [];
  const rawExps = audit.rawSections?.experiences || [];
  if (!items.length) return <p className="text-sm text-muted-foreground">No Experience rewrites generated.</p>;
  // Client-side backfill for legacy audits where the backend didn't
  // attach ``currentCopy`` — match rewrites to parsed roles on the
  // fly so the "Current role summary" tile shows real PDF text.
  const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const used = new Set();
  const resolveCurrent = (rw) => {
    if (rw.currentCopy || rw.originalCopy) return rw.currentCopy || rw.originalCopy;
    const rKey = norm(rw.role);
    const cKey = norm(rw.company);
    // Exact match first.
    let idx = rawExps.findIndex((pe, j) => !used.has(j) && norm(pe.title || pe.role) === rKey && norm(pe.company || pe.employer) === cKey);
    if (idx < 0 && rKey) {
      idx = rawExps.findIndex((pe, j) => {
        if (used.has(j)) return false;
        const pr = norm(pe.title || pe.role);
        const pc = norm(pe.company || pe.employer);
        return (rKey && (rKey.includes(pr) || pr.includes(rKey))) && (!cKey || cKey.includes(pc) || pc.includes(cKey));
      });
    }
    if (idx >= 0) {
      used.add(idx);
      const pe = rawExps[idx];
      return (pe.description || pe.summary || '').trim() || null;
    }
    return null;
  };
  return (
    <div className="space-y-4">
      {items.map((e, i) => (
        <Card key={i} className="border-border shadow-sm" data-testid={`rewrite-experience-item-${i}`}><CardContent className="p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div>
              <p className="text-sm font-semibold">{e.role}{e.company ? ` — ${e.company}` : ''}</p>
              {e.diagnosis && <p className="text-[11.5px] text-muted-foreground max-w-lg">{e.diagnosis}</p>}
            </div>
            <Badge variant="secondary" className="text-[10px] capitalize">{e.sourceReliability || 'inferred'}</Badge>
          </div>
          <BeforeAfter
            before={resolveCurrent(e)}
            after={e.rewrittenCopy}
            beforeLabel="Current role summary"
            afterLabel="Rewritten role summary"
            onCopy={copyText}
            testid={`rewrite-experience-ba-${i}`}
          />
          {(e.proofToCollect || []).length > 0 && (
            <p className="mt-2.5 text-[12px] text-amber-700 dark:text-amber-400"><span className="font-medium">Proof to collect: </span>{e.proofToCollect.join('; ')}</p>
          )}
          {e.mediaSuggestion && <p className="mt-1 text-[12px] text-muted-foreground"><span className="font-medium">Media: </span>{e.mediaSuggestion}</p>}
        </CardContent></Card>
      ))}
    </div>
  );
}

function FeaturedSection({ audit }) {
  const banner = audit.banner || {};
  const featured = audit.featuredRecommendations || [];
  const projects = audit.educationAndProjects || [];
  const hasAnything = banner.headline || featured.length || projects.length;
  if (!hasAnything) return <p className="text-sm text-muted-foreground">No Featured content recommendations generated.</p>;
  const bannerRows = [
    ['Headline', banner.headline],
    ['Supporting line', banner.supportingLine],
    ['Proof line', banner.proofLine],
    ['Call-to-action', banner.cta],
    ['Visual direction', banner.visualDirection],
  ].filter(([, v]) => v);
  return (
    <div className="space-y-4">
      {bannerRows.length > 0 && (
        <Card className="border-border shadow-sm" data-testid="rewrite-featured-banner"><CardContent className="p-4 sm:p-5">
          <p className="text-sm font-semibold mb-1">Banner (top of your profile)</p>
          <p className="text-[11.5px] text-muted-foreground mb-3">Copy each line separately — hand it to your designer or paste into Canva.</p>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="rounded-xl border border-dashed border-border bg-secondary/50 p-3.5">
              <p className="text-[10px] font-mono-edge tracking-[0.18em] uppercase text-muted-foreground mb-1.5">Current banner</p>
              <p className="text-[12.5px] italic text-muted-foreground">Not visible in your PDF — treat the brief as your starting spec.</p>
            </div>
            <div className="rounded-xl border border-primary/40 bg-primary/[0.06] p-3.5 space-y-2">
              <p className="text-[10px] font-mono-edge tracking-[0.18em] uppercase text-primary">Banner brief</p>
              {bannerRows.map(([label, value]) => (
                <div key={label} className="group" data-testid={`rewrite-banner-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10.5px] font-medium uppercase tracking-[0.12em] text-primary/80">{label}</p>
                    <button
                      type="button"
                      onClick={() => copyText(value)}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 inline-flex items-center gap-1 rounded-full bg-primary/10 hover:bg-primary/20 text-primary px-2 py-0.5 text-[11px] font-medium transition-opacity"
                      data-testid={`rewrite-banner-copy-${label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Copy size={10} /> Copy
                    </button>
                  </div>
                  <p className="text-[13px] whitespace-pre-wrap leading-snug">{value}</p>
                </div>
              ))}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => copyText(bannerRows.map(([l, v]) => `${l}: ${v}`).join('\n'))}
                className="w-full mt-1"
                data-testid="rewrite-banner-copy-all"
              >
                <Copy size={12} className="mr-1.5" /> Copy full brief
              </Button>
            </div>
          </div>
          {banner.avoid && <p className="mt-3 text-[12px] text-muted-foreground"><span className="font-medium">Avoid: </span>{banner.avoid}</p>}
        </CardContent></Card>
      )}

      {featured.length > 0 && (
        <Card className="border-border shadow-sm" data-testid="rewrite-featured-assets"><CardContent className="p-4 sm:p-5">
          <p className="text-sm font-semibold mb-2">3–5 Featured assets to pin</p>
          <p className="text-[11.5px] text-muted-foreground mb-3">Pin these under your About section so readers see proof before they scroll.</p>
          <ol className="space-y-2.5">
            {featured.map((f, i) => (
              <li key={i} className="rounded-lg border border-border p-3 bg-card group">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[13px] font-medium">{i + 1}. {f.title || f.type}</p>
                  {(f.example || f.title) && (
                    <button
                      type="button"
                      onClick={() => copyText(f.example || f.title)}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 inline-flex items-center gap-1 rounded-full bg-primary/10 hover:bg-primary/20 text-primary px-2 py-0.5 text-[11px] font-medium transition-opacity"
                      data-testid={`rewrite-featured-copy-${i}`}
                    >
                      <Copy size={10} /> Copy
                    </button>
                  )}
                </div>
                {f.rationale && <p className="text-[12px] text-muted-foreground mt-0.5">{f.rationale}</p>}
                {f.example && <p className="text-[12px] mt-1"><span className="font-medium">Example: </span>{f.example}</p>}
              </li>
            ))}
          </ol>
        </CardContent></Card>
      )}

      {projects.length > 0 && (
        <Card className="border-border shadow-sm" data-testid="rewrite-featured-projects"><CardContent className="p-4 sm:p-5">
          <p className="text-sm font-semibold mb-2">Education, projects &amp; certifications</p>
          <ul className="space-y-2">
            {projects.map((x, i) => (
              <li key={i} className="rounded-lg border border-border p-3 bg-card group">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[13px] font-medium">{x.item} <Badge variant="secondary" className="ml-1 text-[10px] capitalize">{x.type}</Badge></p>
                  {x.recommendation && (
                    <button
                      type="button"
                      onClick={() => copyText(x.recommendation)}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 inline-flex items-center gap-1 rounded-full bg-primary/10 hover:bg-primary/20 text-primary px-2 py-0.5 text-[11px] font-medium transition-opacity"
                      data-testid={`rewrite-project-copy-${i}`}
                    >
                      <Copy size={10} /> Copy
                    </button>
                  )}
                </div>
                {x.diagnosis && <p className="text-[12px] text-muted-foreground mt-0.5">{x.diagnosis}</p>}
                {x.recommendation && <p className="text-[12px] mt-1"><span className="font-medium text-primary">Improve: </span>{x.recommendation}</p>}
              </li>
            ))}
          </ul>
        </CardContent></Card>
      )}
    </div>
  );
}

function SkillsSection({ audit }) {
  const s = audit.skills || {};
  const beforeList = (audit.rawSections?.skills || []);
  const priority = s.priority || [];
  const supporting = s.supporting || [];
  const remove = s.removeRenameOrMove || [];
  return (
    <div className="space-y-4">
      <Card className="border-border shadow-sm"><CardContent className="p-4 sm:p-5">
        <p className="text-sm font-semibold mb-3">Rebuild your Skills strategy</p>
        <BeforeAfter
          before={beforeList.length ? beforeList.join(', ') : null}
          beforeLabel="Current skills"
          after={[...priority.map((x) => x.skill), ...supporting.map((x) => x.skill)].join(', ')}
          afterLabel="Recommended skills (priority first)"
          testid="rewrite-skills-ba"
        />
      </CardContent></Card>

      {priority.length > 0 && (
        <Card className="border-border"><CardContent className="p-4">
          <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-primary mb-2">Priority skills (top 3-5)</p>
          <ul className="space-y-1.5 text-[13px]">{priority.map((sk, i) => <li key={i}><span className="font-medium">{sk.skill}</span>{sk.justification ? ` — ${sk.justification}` : ''}</li>)}</ul>
        </CardContent></Card>
      )}
      {supporting.length > 0 && (
        <Card className="border-border"><CardContent className="p-4">
          <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground mb-2">Supporting skills</p>
          <ul className="space-y-1.5 text-[13px]">{supporting.map((sk, i) => <li key={i}><span className="font-medium">{sk.skill}</span>{sk.justification ? ` — ${sk.justification}` : ''}</li>)}</ul>
        </CardContent></Card>
      )}
      {remove.length > 0 && (
        <Card className="border-border bg-secondary/40"><CardContent className="p-4">
          <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground mb-2">Remove / rename / move</p>
          <ul className="space-y-1.5 text-[13px]">{remove.map((sk, i) => <li key={i}><span className="font-medium">{sk.skill}</span> — {(sk.action || '').replace(/_/g, ' ')}{sk.reason ? `: ${sk.reason}` : ''}</li>)}</ul>
        </CardContent></Card>
      )}
    </div>
  );
}

const SECTION_RENDERERS = {
  headline: HeadlineSection,
  about: AboutSection,
  experience: ExperienceSection,
  featured: FeaturedSection,
  skills: SkillsSection,
};

// -------------------- Recruiter-Lens Toggle --------------------
// Small pill toggle that lets G1 (recruiter-discovery) users flip
// the same profile between the Trajectory lens (sells the next few
// years, momentum evidence) and the Risk lens (sells the last decade,
// named outcomes + conviction by subtraction). Only rendered when the
// audit was produced under a G1 recruiter-visibility objective — which
// we detect via the presence of ``audit.recruiterLens`` set by the
// backend.
function RecruiterLensToggle({ audit, lensVariants = {}, onRegenerateLens, lensRegenerating }) {
  const current = audit?.recruiterLens;
  if (!current || !onRegenerateLens) return null;
  const options = [
    { key: 'trajectory', label: 'Trajectory recruiter', hint: 'Reads for momentum — best when your story is the next 2-5 years' },
    { key: 'risk', label: 'Risk recruiter', hint: 'Reads for verified outcomes — best when your story is 10+ years of proof' },
  ];
  const otherKey = current === 'trajectory' ? 'risk' : 'trajectory';
  const hasArchivedOther = !!lensVariants?.[otherKey];
  return (
    <div
      className="mt-4 rounded-2xl border border-border/70 bg-secondary/40 p-3.5"
      data-testid="recruiter-lens-toggle"
    >
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-primary">
              Recruiter lens
            </p>
            {audit?.recruiterLensAutoPicked && (
              <Badge variant="outline" className="text-[10px] py-0 px-1.5">Auto-picked for your stage</Badge>
            )}
          </div>
          <p className="text-[12px] text-muted-foreground mt-1 max-w-[560px]">
            Same keywords surface both. Different currency of persuasion. Flip the lens to
            see how a hiring manager reads you through the other calculator.
          </p>
        </div>
        <div className="flex items-center gap-1.5" role="tablist" aria-label="Recruiter lens">
          {options.map((opt) => {
            const isCurrent = opt.key === current;
            const busy = lensRegenerating && !isCurrent;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => !isCurrent && !lensRegenerating && onRegenerateLens(opt.key)}
                disabled={isCurrent || lensRegenerating}
                title={opt.hint}
                aria-selected={isCurrent}
                data-testid={`lens-toggle-${opt.key}`}
                className={[
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-medium transition',
                  isCurrent
                    ? 'bg-foreground text-background border-foreground shadow-sm'
                    : 'bg-background text-foreground border-border hover:border-foreground/40 hover:bg-secondary/60',
                  lensRegenerating && !isCurrent ? 'opacity-70 cursor-progress' : '',
                  isCurrent ? 'cursor-default' : '',
                ].join(' ')}
              >
                {busy && <Loader2 size={12} className="animate-spin" />}
                {opt.label}
                {isCurrent && <CheckCircle2 size={12} />}
              </button>
            );
          })}
        </div>
      </div>
      {hasArchivedOther && !lensRegenerating && (
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid="lens-toggle-archive-note">
          You&apos;ve already generated the {otherKey === 'trajectory' ? 'Trajectory' : 'Risk'} lens once — flipping
          back re-runs the LLM so the rewrites stay fresh against your latest profile.
        </p>
      )}
    </div>
  );
}


// -------------------- peer positioning panel (Headline section) --------------------
/**
 * Inline panel that lives inside the Headline rewrite step and lets the
 * buyer regenerate their Headline through the peer-positioning-gap
 * lens. Uses ``/api/analyze/{id}/headline/regenerate-with-positioning``
 * (called via the ``onRegenerate`` prop plumbed down from
 * ``AnalysisV2Layout``). When the result is present we show the peer-
 * positioned Headline variants + the "addresses X gap" callout the
 * user asked for in the Feb-2026 spec.
 */
function PeerPositioningPanel({ onRegenerate, busy, headline, positioning }) {
  if (!onRegenerate) return null;
  const variants = Array.isArray(headline?.variants) ? headline.variants.slice(0, 3) : [];
  const gap = String(headline?.addresses_gap || '').trim();
  const niche = String(
    ((positioning || {}).recommended_positioning || {}).unique_niche || '',
  ).trim();
  const promise = String(
    ((positioning || {}).recommended_positioning || {}).one_line_promise || '',
  ).trim();
  return (
    <div
      className="mt-5 rounded-2xl border border-primary/25 bg-primary/[0.035] p-4 sm:p-5"
      data-testid="peer-positioning-panel"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-9 h-9 shrink-0 rounded-xl bg-primary/10 text-primary grid place-items-center">
          <Target size={16} strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-primary">
              Peer-positioning lens
            </span>
            {niche && (
              <Badge variant="secondary" className="text-[10px] font-mono-edge">
                Niche · {niche}
              </Badge>
            )}
          </div>
          <p className="mt-1 text-sm font-semibold leading-tight">
            Rewrite this Headline to stake out the niche your peer cohort isn&apos;t already crowding.
          </p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Uses the peer benchmark cohort as the "what everyone else is saying" baseline, then
            proposes 3 Headline variants that sit in white-space.
          </p>
        </div>
        <Button
          onClick={onRegenerate}
          disabled={busy}
          className="shrink-0 rounded-full h-10"
          data-testid="rewrite-regen-peer-positioning-btn"
        >
          {busy ? (
            <>
              <Loader2 size={14} className="mr-1.5 animate-spin" /> Regenerating…
            </>
          ) : (
            <>
              <Sparkles size={14} className="mr-1.5" /> Regenerate with peer positioning
            </>
          )}
        </Button>
      </div>

      {variants.length > 0 && (
        <div className="mt-4 space-y-3" data-testid="peer-positioned-headline-variants">
          {gap && (
            <div
              className="rounded-xl border border-primary/30 bg-primary/[0.06] px-3 py-2 text-[12.5px] text-foreground"
              data-testid="peer-positioned-addresses-gap"
            >
              <span className="font-mono-edge text-[10px] tracking-[0.2em] uppercase text-primary mr-1.5">
                Addresses gap
              </span>
              {gap}
            </div>
          )}
          {promise && (
            <p className="text-[12.5px] text-muted-foreground italic">
              Positioning promise · <span className="text-foreground">{promise}</span>
            </p>
          )}
          {variants.map((v, i) => (
            <div
              key={i}
              className="rounded-xl border border-border bg-background p-3 flex items-start gap-3"
              data-testid={`peer-positioned-headline-variant-${i}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">
                  {v?.use_case || `Variant ${i + 1}`}
                </p>
                <p className="text-sm mt-1 leading-snug">{v?.text || ''}</p>
                {Array.isArray(v?.keywords) && v.keywords.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {v.keywords.slice(0, 6).map((k, j) => (
                      <span
                        key={j}
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground"
                      >
                        {k}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => copyText(v?.text || '')}
                className="shrink-0"
                data-testid={`peer-positioned-headline-copy-${i}`}
              >
                <Copy size={13} className="mr-1" /> Copy
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// -------------------- workflow shell --------------------
export default function RewriteWorkflow({ audit, analysisId, sectionKey, progress, onSectionChange, onProgressChange, toneVariants = {}, lensVariants = {}, onRegenerateTone, regenerating, onRegenerateLens, lensRegenerating = false, onRegenerateSection, regeneratingSection = null, onRegenerateHeadlineWithPositioning, positioningRegenerating = false, peerPositionedHeadline = null, peerPositioning = null }) {
  const idx = Math.max(0, REWRITE_SECTIONS.findIndex((s) => s.key === sectionKey));
  const section = REWRITE_SECTIONS[idx];
  const Renderer = SECTION_RENDERERS[section.key];
  const completedCount = REWRITE_SECTIONS.filter((s) => progress[s.key]).length;
  const isLast = idx === REWRITE_SECTIONS.length - 1;
  const currentDone = !!progress[section.key];

  const goBack = () => { if (idx > 0) onSectionChange(REWRITE_SECTIONS[idx - 1].key); };
  const goNext = () => {
    const next = markSectionComplete(analysisId, section.key, true);
    onProgressChange(next);
    if (isLast) {
      toast.success('All 5 rewrite sections marked complete');
      return;
    }
    onSectionChange(REWRITE_SECTIONS[idx + 1].key);
  };
  const undoCurrent = () => {
    const next = markSectionComplete(analysisId, section.key, false);
    onProgressChange(next);
    toast.success(`${section.label} marked incomplete`);
  };
  // Rail-item unchecker — Cmd/Ctrl-click OR the explicit unmark button
  // right of the row.
  const toggleRail = (key) => {
    if (key === section.key) { onSectionChange(key); return; }
    onSectionChange(key);
  };
  const unmark = (key, e) => {
    e.stopPropagation();
    const next = markSectionComplete(analysisId, key, false);
    onProgressChange(next);
    toast.success(`${REWRITE_SECTIONS.find((s) => s.key === key)?.label || 'Section'} marked incomplete`);
  };

  return (
    <div className="grid lg:grid-cols-[1fr_260px] gap-6" data-testid="rewrite-workflow">
      {/* Main column */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground" data-testid="rewrite-breadcrumb">
          <span>Profile Rewrite</span>
          <span>/</span>
          <span className="text-foreground">{section.label}</span>
        </div>

        <p className="mt-3 text-[10px] font-mono-edge tracking-[0.2em] uppercase text-primary">Your next action</p>
        <h2 className="text-2xl sm:text-3xl font-bold mt-1 leading-tight" data-testid="rewrite-title">
          Update your LinkedIn {section.label.toLowerCase()}
        </h2>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="text-[10px]">⏱</span> {section.minutes} minutes</span>
          <span>·</span>
          <span>{section.impact}</span>
        </div>

        <RecruiterLensToggle
          audit={audit}
          lensVariants={lensVariants}
          onRegenerateLens={onRegenerateLens}
          lensRegenerating={lensRegenerating}
        />

        {section.key === 'headline' && (
          <PeerPositioningPanel
            onRegenerate={onRegenerateHeadlineWithPositioning}
            busy={positioningRegenerating}
            headline={peerPositionedHeadline}
            positioning={peerPositioning}
          />
        )}

        <div className="mt-5">
          <Renderer
            audit={audit}
            toneVariants={toneVariants}
            onRegenerateTone={onRegenerateTone}
            regenerating={regenerating}
            onRegenerateSection={onRegenerateSection}
            regeneratingSection={regeneratingSection}
          />
        </div>

        {/* Footer nav */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 sticky bottom-4 z-10">
          <Button
            variant="ghost"
            onClick={goBack}
            disabled={idx === 0}
            data-testid="rewrite-nav-back"
          >
            <ChevronLeft size={14} className="mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            {currentDone && (
              <Button
                variant="ghost"
                size="sm"
                onClick={undoCurrent}
                className="text-muted-foreground hover:text-foreground"
                data-testid="rewrite-nav-mark-incomplete"
              >
                <RotateCcw size={12} className="mr-1.5" /> Mark incomplete
              </Button>
            )}
            <Button
              onClick={goNext}
              className="rounded-full bg-foreground text-background hover:bg-foreground/90 shadow-lg"
              data-testid="rewrite-nav-continue"
            >
              <CheckCircle2 size={14} className="mr-2" />
              {isLast ? "I've updated it — Mark all complete" : "I've updated it — Continue"} <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Right progress rail */}
      <aside className="lg:sticky lg:top-24 self-start" data-testid="rewrite-progress-rail">
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">Profile Rewrite</p>
          <p className="text-sm font-semibold mt-0.5">{completedCount} of {REWRITE_SECTIONS.length} complete</p>
          <div className="mt-3 h-1.5 rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${(completedCount / REWRITE_SECTIONS.length) * 100}%` }}
            />
          </div>
          <ol className="mt-4 space-y-1">
            {REWRITE_SECTIONS.map((s, i) => {
              const done = !!progress[s.key];
              const active = s.key === section.key;
              return (
                <li key={s.key}>
                  <div
                    className={`group w-full flex items-center gap-2.5 rounded-lg px-2 py-2 transition-colors ${
                      active ? 'bg-primary/10 text-foreground' : 'hover:bg-secondary text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleRail(s.key)}
                      className="flex items-center gap-2.5 text-left flex-1 min-w-0"
                      data-testid={`rewrite-rail-item-${s.key}`}
                    >
                      <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold shrink-0 ${
                        done ? 'bg-primary text-primary-foreground' : active ? 'bg-primary/20 text-primary' : 'bg-secondary text-muted-foreground'
                      }`}>
                        {done ? <CheckCircle2 size={13} /> : i + 1}
                      </span>
                      <span className={`text-[13px] truncate ${active ? 'font-semibold text-foreground' : ''}`}>{s.label}</span>
                    </button>
                    {done && (
                      <button
                        type="button"
                        onClick={(e) => unmark(s.key, e)}
                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 h-5 w-5 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-opacity shrink-0"
                        aria-label={`Mark ${s.label} incomplete`}
                        title="Mark incomplete"
                        data-testid={`rewrite-rail-unmark-${s.key}`}
                      >
                        <X size={11} />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
          <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
            Progress is saved locally to this browser.
          </p>
        </div>
      </aside>
    </div>
  );
}

export { REWRITE_SECTIONS };
